/**
 * OdotID - Permacookie for user identification
 *
 * Generates and stores a persistent anonymous ID for:
 * - Tracking user search history (last 25 searches)
 * - Personalizing experience without login
 *
 * Uses both localStorage AND cookies for resilience against
 * Safari ITP and other browser privacy features that may clear
 * localStorage after periods of inactivity.
 */

const ODOT_ID_KEY = 'tastymusic_odot_id';
const COOKIE_NAME = 'tm_odot';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

/**
 * Generate a random ID (similar to share ID format)
 */
function generateId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 12; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Get odotId from cookie
 */
function getFromCookie(): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${COOKIE_NAME}=([^;]+)`));
  return match ? match[2] : null;
}

/**
 * Set odotId in cookie (1 year expiry, SameSite=Lax for security)
 */
function setInCookie(odotId: string): void {
  document.cookie = `${COOKIE_NAME}=${odotId}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

/**
 * Get or create the user's OdotID
 *
 * Checks both localStorage and cookies for resilience.
 * If found in one but not the other, syncs them.
 * Only generates a new ID if not found in either.
 */
export function getOdotId(): string {
  // Check both storage locations
  const fromLocalStorage = localStorage.getItem(ODOT_ID_KEY);
  const fromCookie = getFromCookie();

  let odotId: string;

  if (fromLocalStorage && fromCookie) {
    // Both exist - prefer localStorage (most recent user activity)
    // Sync cookie to match for future resilience
    odotId = fromLocalStorage;
    if (fromLocalStorage !== fromCookie) {
      setInCookie(fromLocalStorage);
    }
  } else if (fromLocalStorage) {
    // Only in localStorage - sync to cookie
    odotId = fromLocalStorage;
    setInCookie(odotId);
  } else if (fromCookie) {
    // Only in cookie (localStorage was cleared) - restore to localStorage
    odotId = fromCookie;
    localStorage.setItem(ODOT_ID_KEY, odotId);
  } else {
    // Neither exists - generate new ID and store in both
    odotId = generateId();
    localStorage.setItem(ODOT_ID_KEY, odotId);
    setInCookie(odotId);
  }

  return odotId;
}

/**
 * Get headers with OdotID for API requests
 */
export function getAuthHeaders(): Record<string, string> {
  return {
    'X-Odot-ID': getOdotId(),
  };
}
